import FVector from '@data/geometry/FVector';
import { Box, Card, IconButton, Stack, TextField, Typography } from '@mui/material';
import { Trash04 } from '@untitledui-pro/icons/line';
import React from 'react';

type PolygonPointsListProps = {
    points: FVector[];
    onChange: (newPoints: FVector[]) => void;
    onRemove: (index: number) => void;
    isEditingPolygon: boolean;
};

export const PolygonPointsList: React.FC<PolygonPointsListProps> = ({ points, onChange, onRemove, isEditingPolygon }) => {
    const handlePointChange = (index: number, axis: 'x' | 'y' | 'z', value: string) => {
        const parsed = Number(value.replace(',', '.'));
        if (Number.isNaN(parsed)) return;
        const newPoints = points.map((p, i) => {
            if (i !== index) return p;
            const updated = p.clone();
            updated[axis] = parsed;
            return updated;
        });
        onChange(newPoints);
    };

    const handleRemove = (index: number) => {
        onRemove(index);
    };

    const blockTyping = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const allowedKeys = [
            '0',
            '1',
            '2',
            '3',
            '4',
            '5',
            '6',
            '7',
            '8',
            '9',
            '.',
            ',',
            '-',
            'ArrowUp',
            'ArrowDown',
            'Tab',
            'Shift',
            'Backspace',
            'Delete',
            'Home',
            'End',
            'Control',
            'Meta',
            'Alt',
            'Escape',
            'Enter',
        ];
        if (!allowedKeys.includes(e.key)) {
            e.preventDefault();
        }
    };

    const disableDelete = points.length <= 3 || !isEditingPolygon;

    return (
        <Box sx={{ ml: -1 }}>
            <Stack direction="row" spacing={0.5} alignItems="flex-end" sx={{ mb: -0.8 }}>
                <Box sx={{ minWidth: 20 }} />
                <Typography sx={{ fontSize: 9, width: 50, textAlign: 'center' }}>X</Typography>
                <Typography sx={{ fontSize: 9, width: 50, textAlign: 'center' }}>Y</Typography>
                <Typography sx={{ fontSize: 9, width: 50, textAlign: 'center' }}>Z</Typography>
                <Box sx={{ width: 32 }} />
            </Stack>
            {points.map((point, i) => (
                <Card
                    key={i}
                    variant="outlined"
                    sx={{
                        mb: 0,
                        p: 0,
                        bgcolor: 'transparent',
                        border: '1px solid transparent',
                        borderRadius: 1,
                    }}
                >
                    <Stack direction="row" spacing={0.5} alignItems="flex-end">
                        {/* Point label */}
                        <Typography sx={{ fontWeight: 600, minWidth: 20, fontSize: 11, textAlign: 'center' }}>P{i}</Typography>

                        {/* X coordinate */}
                        <Stack spacing={0} alignItems="center">
                            <TextField
                                variant="outlined"
                                value={Number.isFinite(point.x) ? point.x : ''}
                                size="small"
                                sx={{ width: 50 }}
                                disabled={!isEditingPolygon}
                                inputProps={{ step: 0.01 }}
                                InputProps={{
                                    sx: {
                                        padding: 0,
                                        input: {
                                            padding: '1px',
                                            textAlign: 'center',
                                            fontSize: 10,
                                            cursor: 'default',
                                        },
                                    },
                                }}
                                type="number"
                                onKeyDown={blockTyping}
                                onPaste={(e) => e.preventDefault()}
                                onChange={(e) => handlePointChange(i, 'x', e.target.value)}
                            />
                        </Stack>

                        {/* Y coordinate */}
                        <Stack spacing={0} alignItems="center">
                            <TextField
                                variant="outlined"
                                value={Number.isFinite(point.y) ? point.y : ''}
                                size="small"
                                sx={{ width: 50 }}
                                disabled={!isEditingPolygon}
                                inputProps={{ step: 0.01 }}
                                InputProps={{
                                    sx: {
                                        padding: 0,
                                        input: {
                                            padding: '1px',
                                            textAlign: 'center',
                                            fontSize: 10,
                                            cursor: 'default',
                                        },
                                    },
                                }}
                                type="number"
                                onKeyDown={blockTyping}
                                onPaste={(e) => e.preventDefault()}
                                onChange={(e) => handlePointChange(i, 'y', e.target.value)}
                            />
                        </Stack>

                        {/* Z coordinate */}
                        <Stack spacing={0} alignItems="center">
                            <TextField
                                variant="outlined"
                                value={Number.isFinite(point.z) ? point.z : ''}
                                size="small"
                                sx={{ width: 50 }}
                                disabled={!isEditingPolygon}
                                inputProps={{ step: 0.01 }}
                                InputProps={{
                                    sx: {
                                        padding: 0,
                                        input: {
                                            padding: '1px',
                                            textAlign: 'center',
                                            fontSize: 10,
                                            cursor: 'default',
                                        },
                                    },
                                }}
                                type="number"
                                onKeyDown={blockTyping}
                                onPaste={(e) => e.preventDefault()}
                                onChange={(e) => handlePointChange(i, 'z', e.target.value)}
                            />
                        </Stack>

                        {/* Delete button */}
                        <IconButton
                            color={disableDelete ? 'default' : 'error'}
                            onClick={() => handleRemove(i)}
                            sx={{
                                ml: 0.5,
                                alignSelf: 'stretch',
                                display: 'flex',
                                alignItems: 'flex-end',
                                pb: 0.2,
                            }}
                            disabled={disableDelete}
                        >
                            <Trash04 className="icon-size-medium" />
                        </IconButton>
                    </Stack>
                </Card>
            ))}
        </Box>
    );
};
